import { z } from "zod";
import {
  phonePattern,
  maritalStatusValues,
  australianStates,
  employmentStatusValues,
  employmentTypeValues,
} from "./shared";

// --- Address ---

export const addressSchema = z.object({
  address: z.string().min(1, "Address is required"),
  zip: z.string().min(1, "Zip code is required"),
  city: z.string().min(1, "City is required"),
  state: z.enum(australianStates, { message: "State is required" }),
  addressHeldSince: z.coerce.date().optional(),
});
export type AddressFormData = z.infer<typeof addressSchema>;

// --- Personal details ---

export const applicantPersonalSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  dateOfBirth: z.coerce.date({ message: "Date of birth is required" }),
  maritalStatus: z.enum(maritalStatusValues, { message: "Marital status is required" }),
  emailAddress: z.string().email("A valid email is required"),
  phone: z.string().regex(phonePattern, "A valid phone number is required"),
});
export type ApplicantPersonalFormData = z.infer<typeof applicantPersonalSchema>;

// --- ID ---

export const applicantIdSchema = z.object({
  driverLicenseNumber: z.string().min(1, "Driver license number is required"),
  driverLicenseCardNumber: z.string().min(1, "Driver license card number is required"),
  driverLicenseExpiryDate: z.coerce.date({ message: "License expiry date is required" }),
});
export type ApplicantIdFormData = z.infer<typeof applicantIdSchema>;

// --- Current mortgage ---

export const currentMortgageSchema = z.object({
  mortgageHeldWith: z.string().min(1, "Mortgage lender is required"),
});
export type CurrentMortgageFormData = z.infer<typeof currentMortgageSchema>;

// --- Employment ---

export const baseEmploymentSchema = z.object({
  employmentStatus: z.enum(employmentStatusValues, { message: "Employment status is required" }),
  currentJobStartDate: z.coerce.date().optional(),
  employerName: z.string().optional(),
  employmentType: z.enum(employmentTypeValues).optional(),
  companyAddress: z.string().optional(),
  previousEmployer: z.string().optional(),
  previousJobStartDate: z.coerce.date().optional(),
  previousJobEndDate: z.coerce.date().optional(),
  accountantDetails: z.string().optional(),
  abnNumber: z.string().optional(),
});

export const employmentSchema = baseEmploymentSchema.superRefine((data, ctx) => {
  if (data.employmentStatus === "employed") {
    if (!data.employerName) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Employer name is required", path: ["employerName"] });
    }
    if (!data.employmentType) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Employment type is required", path: ["employmentType"] });
    }
    if (!data.companyAddress) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Company address is required", path: ["companyAddress"] });
    }
  }
  if (data.employmentStatus === "self-employed") {
    if (!data.accountantDetails) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Accountant details are required",
        path: ["accountantDetails"],
      });
    }
    if (!data.abnNumber) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "ABN number is required", path: ["abnNumber"] });
    }
  }
});
export type EmploymentFormData = z.infer<typeof baseEmploymentSchema>;

// --- Form wrapper schemas (nested under mainApplicant/coApplicant for multi-step forms) ---

export const applicantsPersonalSchema = z.object({
  mainApplicant: applicantPersonalSchema.optional(),
  coApplicant: applicantPersonalSchema.optional(),
});

const applicantAddressGroupSchema = z.object({
  address: addressSchema.optional(),
  previousAddress: addressSchema.optional(),
});

export const applicantsAddressSchema = z.object({
  mainApplicant: applicantAddressGroupSchema.optional(),
  coApplicant: applicantAddressGroupSchema.optional(),
});

export const applicantsEmploymentSchema = z.object({
  mainApplicant: baseEmploymentSchema.optional(),
  coApplicant: baseEmploymentSchema.optional(),
});

export const applicantsMortgageSchema = z.object({
  mainApplicant: currentMortgageSchema.optional(),
  coApplicant: currentMortgageSchema.optional(),
});

export const applicantsIdSchema = z.object({
  mainApplicant: applicantIdSchema.optional(),
  coApplicant: applicantIdSchema.optional(),
});
